import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { BUILD_STAGES, WORKING_TERMS, ENGAGEMENT_MODELS, PRICING_FACTORS } from "@/data/content";
import { PageHero, SectionHead, CtaBand, BulletList } from "@/components/Blocks";
import { Cta } from "@/components/Cta";
import { Reveal, Stagger, itemVariants, EASE } from "@/components/motion";
import { useSeo } from "@/lib/seo";

const ENGINEERING = [
  ["Right-sized architecture", "We choose the simplest architecture that will hold up for your next stage — and document why."],
  ["Mainstream, maintainable stacks", "Well-supported technologies your future team can hire for and maintain."],
  ["Quality where it counts", "Automated checks and testing focused on the workflows that matter most to users and revenue."],
  ["Security by default", "Secrets kept server-side, least-privilege access, validated inputs, and sensible data handling."],
  ["Observability", "Logging and monitoring so problems are found by us, not by your customers."],
  ["AI with evaluation", "When AI is involved, we define how quality is measured before shipping it."],
];

export function HowWeBuild() {
  useSeo("How We Build", "SparkIdea Interactive's product development process — from discovery and specification to design, iterative development, deployment, and optimization.");
  return (
    <>
      <PageHero testId="how-hero" eyebrow="How we build" title={["A clear path", "from idea to launch."]}
        intro="A transparent, iterative process with working software at every step — and clear expectations on communication, approvals, scope, and ownership.">
        <Cta to="/contact" size="lg" testId="how-hero-cta">Start a Project</Cta>
      </PageHero>
      <section className="border-t border-si-line py-20 sm:py-28">
        <div className="wrap">
          <SectionHead eyebrow="The process" title="Eight stages. No black boxes." />
          <div className="relative mt-16">
            <div className="absolute bottom-0 left-[19px] top-0 w-px bg-si-line md:left-1/2" aria-hidden="true" />
            <ol className="space-y-12">
              {BUILD_STAGES.map(([t, d, deliver], i) => (
                <motion.li key={t} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.7, ease: EASE }} className={`relative grid gap-6 pl-14 md:grid-cols-2 md:gap-16 md:pl-0 ${i % 2 ? "md:[&>div:first-child]:order-2" : ""}`} data-testid={`build-stage-${i}`}>
                  <span className="absolute left-0 top-1 z-10 grid h-10 w-10 place-items-center rounded-full border border-si-cyan bg-si-bg font-mono text-xs text-si-cyan md:left-1/2 md:-translate-x-1/2">0{i + 1}</span>
                  <div className={i % 2 ? "md:pl-10" : "md:pr-10 md:text-right"}>
                    <h3 className="text-2xl font-bold tracking-tight sm:text-3xl">{t}</h3>
                    <p className="mt-3 text-base leading-relaxed text-si-muted">{d}</p>
                  </div>
                  <div className={i % 2 ? "md:pr-10" : "md:pl-10"}>
                    <div className="panel p-5">
                      <p className="eyebrow mb-3 !text-si-muted">Example deliverables</p>
                      <ul className="flex flex-wrap gap-2">{deliver.map((x) => <li key={x} className="rounded-full border border-si-line bg-si-bg px-3 py-1.5 text-sm">{x}</li>)}</ul>
                    </div>
                  </div>
                </motion.li>
              ))}
            </ol>
          </div>
        </div>
      </section>
      <section id="engineering" className="scroll-mt-24 border-t border-si-line bg-si-bg2 py-20 sm:py-28">
        <div className="wrap">
          <SectionHead eyebrow="Technology & engineering" title="How we make technical calls." />
          <Stagger className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {ENGINEERING.map(([t, d]) => (
              <motion.div key={t} variants={itemVariants} className="panel p-7"><h3 className="text-xl font-bold">{t}</h3><p className="mt-3 text-base leading-relaxed text-si-muted">{d}</p></motion.div>
            ))}
          </Stagger>
        </div>
      </section>
      <section className="py-20 sm:py-28">
        <div className="wrap grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-4"><SectionHead eyebrow="Working together" title="Expectations, made explicit." /></div>
          <div className="divide-y divide-si-line border-y border-si-line lg:col-span-8">
            {WORKING_TERMS.map(([t, d]) => (
              <Reveal key={t} className="grid gap-3 py-7 sm:grid-cols-3"><h3 className="text-xl font-bold">{t}</h3><p className="text-base leading-relaxed text-si-muted sm:col-span-2">{d}</p></Reveal>
            ))}
          </div>
        </div>
      </section>
      <CtaBand testId="how-cta" />
    </>
  );
}

export function EngagementModels() {
  useSeo("Engagement Models", "Ways to work with SparkIdea Interactive: discovery, project-based, monthly engineering partnership, fractional CTO, and venture partnership.");
  return (
    <>
      <PageHero testId="engagement-hero" eyebrow="Engagement models" title={["Structured around", "what you actually need."]}
        intro="Every engagement is shaped around your stage, scope, and goals. Here are the commercial structures we offer." />
      <section className="border-t border-si-line py-20 sm:py-28">
        <div className="wrap">
          <Stagger className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {ENGAGEMENT_MODELS.map(([t, d, inc], i) => (
              <motion.div key={t} variants={itemVariants} data-testid={`engagement-${i}`}
                className={`panel flex flex-col p-8 transition-colors hover:border-si-cyan/50 ${i === 4 ? "border-si-orange/30 lg:col-span-1" : ""}`}>
                <span className="font-mono text-xs text-si-cyan">0{i + 1}</span>
                <h3 className="mt-6 text-2xl font-bold tracking-tight">{t}</h3>
                <p className="mt-3 text-base leading-relaxed text-si-muted">{d}</p>
                <ul className="mt-6 space-y-2">{inc.map((x) => <li key={x} className="flex items-center gap-2 text-sm"><Check className="h-4 w-4 text-si-cyan" />{x}</li>)}</ul>
              </motion.div>
            ))}
          </Stagger>
        </div>
      </section>
      <section className="border-t border-si-line bg-si-paper py-20 text-si-ink sm:py-28">
        <div className="wrap grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5"><SectionHead light eyebrow="Pricing" title="How estimates are made." intro="We don't publish fixed prices because every product is different. After an initial conversation, we propose a scope and estimate based on:" /></div>
          <Reveal className="lg:col-span-7"><div className="rounded-3xl border border-[#dde3ee] bg-white p-8 sm:p-10"><BulletList light items={PRICING_FACTORS} /></div></Reveal>
        </div>
      </section>
      <CtaBand testId="engagement-cta" title="Let's find the right fit." primary="Discuss Your Project" />
    </>
  );
}
